import { Appearance, Inventory } from "game-common/models"
import { Graphics } from "pixi.js"

import { ClientGameLogic } from "../../../../client_game_logic"
import { GuiManager } from "../gui_manager"
import { BuildPage } from "./character_creation/build_page"
import { BuilderHost, BuilderPage, NavButtonMeta, PageId } from "./character_creation/page"
import { ControlsPage, IntroPage, JourneyPage } from "./character_creation/story_pages"
import {
    ACCENT,
    BACKDROP,
    CONTENT_H,
    CONTENT_W,
    FOOTER_H,
    HEADER_H,
    RIM,
    RIM_DIM,
    TEXT_DIM,
} from "./character_creation/theme"
import { PillButton, text } from "./character_creation/widgets"
import { Fader } from "./fader"
import { FullScreenModal } from "./fullscreen_modal"
import { stageViewport } from "../../../../canvas_layout"
import { setContentWidth } from "./character_creation/theme"

// The character-creation screen.
//
// Four pages share one frame -- a header, the page's body, and a footer of actions the page decides
// on: the exile that opens the game, the character forge itself, the briefing, and the controls
// reference. The forge is the substance of it; see character_creation/build_page.ts.
//
// The screen is only repainted when something actually changes (a page switch, a palette click, the
// player data finally arriving), rather than on every player-stat update the HUD receives -- the
// pages hold live state (which trait is open, how far the intro has typed) that a blind rebuild
// would throw away.

export class CharacterBuilder extends FullScreenModal implements BuilderHost {
    logic: ClientGameLogic

    private pages: Map<PageId, BuilderPage> = new Map()
    private currentPageId: PageId = "intro"
    private navButtons: NavButtonMeta[] = []
    private initialized = false
    private dirty = true
    private wasOpen = false
    private viewportHeight = 0
    private refreshTimer: any
    // A repaint only fades the body up when the body is genuinely new -- opening the screen, or
    // moving to another page. An in-place repaint (a palette click, a pager step) redraws the same
    // page, and fading that from nothing is read as the whole screen blinking.
    private fadeNextPaint = true

    constructor(guiManager: GuiManager, logic: ClientGameLogic) {
        super(guiManager, {
            opaque: true,
            unescapable: true,
        })
        this.logic = logic
    }

    // The HUD calls this whenever the player entity changes. Rebuild only when the screen has
    // something new to show -- but keep retrying initialization, because the first call usually
    // arrives before the player's appearance and starting kit have.
    update = () => {
        // Opening (toggle -> update) always paints, even if nothing changed since it last closed.
        const open = this.isOpen()
        if (open && !this.wasOpen) {
            this.dirty = true
            this.fadeNextPaint = true
        }
        this.wasOpen = open

        if (!this.initialized && !this.initialize()) {
            return
        }
        if (!this.dirty) {
            return
        }
        this.dirty = false
        this.rebuildSurface()
    }

    // Frame hook (driven by GuiManager.updateTimer) for the page's own animation: the ember glow on
    // the forge's stage, the intro's typewriter.
    animate = (dtMs: number) => {
        if (!this.isOpen()) {
            return
        }
        if (CONTENT_W !== stageViewport().width - 20 || this.viewportHeight !== stageViewport().height) {
            this.dirty = true
            this.update()
        }
        this.pages.get(this.currentPageId)?.animate(dtMs)
    }

    private initialize = (): boolean => {
        const player = this.guiManager.getPlayer()

        if (!player?.appearance || player.inventory?.items?.length < 1) {
            return false
        }

        const register = (page: BuilderPage) => {
            page.host = this
            this.pages.set(page.pageId, page)
        }

        register(new IntroPage())
        register(new BuildPage(player))
        register(new JourneyPage())
        register(new ControlsPage())

        this.resetNavButtons()
        this.initialized = true
        return true
    }

    // --- BuilderHost -------------------------------------------------------------------------

    // Repaint, but not from inside the click that asked for it: a palette click would otherwise tear
    // down the very chip pixi is still dispatching the event on. Coalesces a burst into one repaint.
    refresh = () => {
        this.dirty = true
        if (this.refreshTimer !== undefined) {
            return
        }
        this.refreshTimer = setTimeout(() => {
            this.refreshTimer = undefined
            if (this.isOpen()) {
                this.update()
            }
        }, 0)
    }

    setNavButtons = (buttons: NavButtonMeta[]) => {
        this.navButtons = buttons
    }

    resetNavButtons = () => {
        this.navButtons = [
            {
                title: "Edit character",
                callback: () => this.switchPage("build"),
            },
            {
                title: "Review controls",
                callback: () => this.switchPage("controls"),
            },
            {
                title: "Let's go! ▸",
                primary: true,
                callback: () => this.close(),
            },
        ]
    }

    submitCharacter = (appearance: Appearance, inventory: Inventory) => {
        this.logic.playerController.client.saveAppearance(appearance, inventory)
    }

    switchPage = (pageId: PageId) => {
        if (pageId === this.currentPageId) {
            return
        }
        this.pages.get(this.currentPageId)?.tearDown()
        this.currentPageId = pageId
        this.fadeNextPaint = true
        this.refresh()
    }

    // --- rendering ---------------------------------------------------------------------------

    createContent = (): Graphics => {
        const main = new Graphics()
        const viewport = stageViewport()
        this.viewportHeight = viewport.height
        setContentWidth(viewport.width - 20)

        this.drawBackdrop(main)

        if (!this.initialized && !this.initialize()) {
            // Player data hasn't landed yet; the next update() tries again.
            return main
        }

        const page = this.pages.get(this.currentPageId)
        page.build()
        main.addChild(page)

        this.drawHeader(main, page.title, page.hint)
        this.drawFooter(main)

        // On a page switch everything but the backdrop fades up together, so the move reads as one.
        // A repaint of the page you are already on has to land instantly -- the whole rebuild
        // happens inside one task, so swapping the children is invisible unless we fade them.
        if (this.fadeNextPaint) {
            this.fadeNextPaint = false
            page.alpha = 0
            new Fader(page, { direction: "in", minAlpha: 1.0, fadeAlphaDecrement: 0.06 }).update()
        } else {
            // Pages are long-lived objects, so a fade left in flight by an earlier paint would
            // otherwise hand this one a half-transparent page.
            page.alpha = 1
        }

        return main
    }

    // A warm, dark hall for the character to stand in -- the modal's own backdrop is flat black.
    // Drawn out past the content box so it covers the dialog's padding too.
    private drawBackdrop = (g: Graphics) => {
        const viewport = stageViewport()
        g.beginFill(BACKDROP, 1)
        g.drawRect(-12, -12, CONTENT_W + 24, Math.max(CONTENT_H + 44, viewport.height + 24))
        g.endFill()

        // Firelight welling up from the floor of the screen: three stacked ellipses, brightest at the
        // core. A handful of draw calls instead of a blur filter (see the budget-hardware note).
        const cx = CONTENT_W / 2
        const cy = CONTENT_H - 40
        g.beginFill(0xff4a12, 0.05)
        g.drawEllipse(cx, cy, CONTENT_W * 0.52, 250)
        g.endFill()
        g.beginFill(0xff8a24, 0.045)
        g.drawEllipse(cx, cy, 300, 180)
        g.endFill()
        g.beginFill(0xffcf5a, 0.03)
        g.drawEllipse(cx, cy, 160, 110)
        g.endFill()

        // Vignette: darker at the rim, so the panels sit forward of the background.
        for (let i = 0; i < 4; i++) {
            g.lineStyle(14, 0x000000, 0.1)
            g.drawRect(-12 + i * 12, -12 + i * 12, CONTENT_W + 24 - i * 24,
                Math.max(CONTENT_H + 44, viewport.height + 24) - i * 24)
        }
        g.lineStyle(0)
    }

    private drawHeader = (g: Graphics, title: string, hintText?: string) => {
        const heading = text(title.toUpperCase(), { role: "compactTitle", color: ACCENT, bold: true })
        heading.x = 2
        heading.y = 4
        g.addChild(heading)

        if (hintText) {
            const hint = text(hintText, { role: "compactCaption", color: TEXT_DIM })
            hint.x = CONTENT_W - hint.width - 2
            hint.y = 10
            hint.alpha = 0.75
            g.addChild(hint)
        }

        const rule = new Graphics()
        rule.beginFill(RIM, 0.55)
        rule.drawRect(0, HEADER_H - 6, CONTENT_W, 2)
        rule.endFill()
        rule.beginFill(RIM_DIM, 0.35)
        rule.drawRect(0, HEADER_H - 4, CONTENT_W, 1)
        rule.endFill()
        g.addChild(rule)
    }

    private drawFooter = (g: Graphics) => {
        const y = CONTENT_H - FOOTER_H

        const rule = new Graphics()
        rule.beginFill(RIM_DIM, 0.45)
        rule.drawRect(0, y, CONTENT_W, 1)
        rule.endFill()
        g.addChild(rule)

        const buttonY = y + 10
        let x = 2
        const primary = this.navButtons.filter(meta => meta.primary)
        const secondary = this.navButtons.filter(meta => !meta.primary)

        secondary.forEach(meta => {
            const button = new PillButton({ label: meta.title, onClick: meta.callback })
            button.x = x
            button.y = buttonY
            g.addChild(button)
            x += button.boxWidth + 8
        })

        let right = CONTENT_W - 2
        primary
            .slice()
            .reverse()
            .forEach(meta => {
                const button = new PillButton({ label: meta.title, primary: true, onClick: meta.callback })
                button.x = right - button.boxWidth
                button.y = buttonY
                g.addChild(button)
                right -= button.boxWidth + 8
            })
    }
}
